import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import QrScanner from '../../components/QrScanner';
import CheckInForm from '../../components/CheckInForm';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import { post } from '../../api/client';
import { Card, Button, SectionTitle } from '../../components/UI';
import { fmtDate, fmtTime } from '../../utils/format';
import { readSessionCode } from './shared';

/**
 * Self check-in: a session code is marked the moment it is read, a "Done" card
 * confirms it, and the page returns home by itself.
 *
 * Some events ask a question or two at the gate — who you are, and how many
 * people came with you. The scan then comes back asking for that form, and
 * attendance is recorded when it is answered.
 */
export default function AttendeeCheckIn() {
  const { events } = useEvents();
  const toast = useToast();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [asking, setAsking] = useState(null);   // { code, form, event, session }
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  /** Sends the scan, with the check-in answers once we have them. */
  const mark = async (code, context, responses) => {
    setBusy(true);
    try {
      const res = await post('/attendance/self-scan', { code, ...(responses ? { responses } : {}) });
      if (res.data?.needsForm) {
        setAsking({ code, form: res.data.form, ...context });
        return;
      }
      setAsking(null);
      setDone({
        ...context,
        already: Boolean(res.data?.duplicate),
        people: res.data?.people || 1,
        companions: res.data?.companions ?? null,
        partyState: res.data?.partyState || null,
      });
      timer.current = setTimeout(() => nav('/attendee', { replace: true }), 3200);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onScan = async (code) => {
    const parsed = readSessionCode(code);
    if (!parsed) return toast.error('That is not a session code. Scan the code shown at the session.');
    const event = events.find((e) => String(e._id) === parsed.eventId);
    const session = event?.sessions?.find((s) => String(s._id) === parsed.sessionId);
    if (!event || !session) return toast.error('This session is not part of your events.');
    return mark(code, { event, session });
  };

  const partyLine = () => {
    if (done.companions === null || done.companions === undefined) return null;
    if (!done.companions) return 'Checked in on your own.';
    const n = done.companions;
    return `${done.people} people checked in — you and ${n} ${n === 1 ? 'other' : 'others'}.`;
  };

  return (
    <>
      <SectionTitle
        title="Check in"
        subtitle={asking ? 'Just a couple of details' : 'Scan the code shown at your session'}
      />
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
            <Card padding={28} style={{ textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: 99, background: 'rgba(74,222,128,0.14)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                <CheckCircle2 size={32} color="var(--ok)" />
              </div>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--ok)' }}>{done.already ? 'Already marked' : 'Done!'}</h2>
              <p style={{ fontSize: 17, fontWeight: 600, marginTop: 8 }}>{done.session.title}</p>
              <p style={{ fontSize: 13.5, color: 'var(--text-mid)', marginTop: 4 }}>
                {fmtDate(done.session.startAt)} · {fmtTime(done.session.startAt)}{done.session.endAt ? ` – ${fmtTime(done.session.endAt)}` : ''}
              </p>
              <p style={{ fontSize: 13, color: 'var(--gold-400)', marginTop: 3 }}>{done.event.title}</p>
              <p style={{ fontSize: 13, color: 'var(--text-lo)', marginTop: 14 }}>
                {done.already ? 'Your attendance was already recorded.' : 'Your attendance is recorded. Enjoy the session!'}
              </p>
              {partyLine() && (
                <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 6 }}>{partyLine()}</p>
              )}
              <Button size="lg" onClick={() => { clearTimeout(timer.current); nav('/attendee', { replace: true }); }} style={{ width: '100%', marginTop: 20 }}>
                Back to home
              </Button>
            </Card>
          </motion.div>
        ) : asking ? (
          <CheckInForm
            form={asking.form}
            busy={busy}
            onSubmit={(responses) => mark(asking.code, { event: asking.event, session: asking.session }, responses)}
            onCancel={() => setAsking(null)}
          />
        ) : (
          <Card padding={20}>
            <QrScanner onScan={onScan} busy={busy} hint={busy ? 'Marking you present…' : 'Point at the code displayed at the session'} />
          </Card>
        )}
      </div>
    </>
  );
}
